/**
 * RecordTransactionModal Component
 * Shows when a financial task is completed, offering to record it as a transaction
 * Similar to how shopping list "Mark as Bought" creates transactions
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Receipt, 
  X, 
  Banknote, 
  Calendar,
  Tag,
  CheckCircle2,
  Loader2,
  Sparkles
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { createTransaction } from '@/services/transactionService';
import { useAuth } from '@/contexts/AuthContext';
import { useCategoryEnvelope } from '@/context/CategoryEnvelopeContext';
import toast from 'react-hot-toast';

export default function RecordTransactionModal({
  isOpen,
  onClose,
  task,
  onSuccess
}) {
  const { user, currency: userCurrency } = useAuth();
  const currency = userCurrency || 'GBP';
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);
  
  // Get envelope context for budget sync
  let categoryEnvelopeContext = null;
  try {
    categoryEnvelopeContext = useCategoryEnvelope();
  } catch {
    // Context not available
  }

  // Pre-fill amount from task's estimatedAmount
  useEffect(() => {
    if (task?.estimatedAmount) {
      setAmount(task.estimatedAmount.toString());
    }
  }, [task?.estimatedAmount]);

  // Pre-fill date from task's dueDate or completion date
  useEffect(() => {
    if (task?.dueDate) {
      // Handle various date formats (Firestore Timestamp, Date object, string)
      let dateValue;
      if (task.dueDate.toDate) {
        // Firestore Timestamp
        dateValue = task.dueDate.toDate();
      } else if (task.dueDate instanceof Date) {
        dateValue = task.dueDate;
      } else if (typeof task.dueDate === 'string') {
        dateValue = new Date(task.dueDate);
      } else {
        dateValue = new Date();
      }
      // Format to YYYY-MM-DD for input[type="date"]
      setDate(dateValue.toISOString().split('T')[0]);
    } else {
      setDate(new Date().toISOString().split('T')[0]);
    }
  }, [task?.dueDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSaving(true);
    try {
      // Get the envelope data to find the correct categoryId
      let categoryId = 'other-expenses'; // Default fallback
      
      // If we have a linked envelope, try to get the category from context
      if (task.linkedEnvelopeId && categoryEnvelopeContext?.envelopes) {
        const envelope = categoryEnvelopeContext.envelopes.find(
          env => env.id === task.linkedEnvelopeId || env.envelopeId === task.linkedEnvelopeId
        );
        if (envelope?.linkedCanonicalCategory) {
          categoryId = envelope.linkedCanonicalCategory;
        } else if (envelope?.category) {
          categoryId = envelope.category;
        }
      }
      
      // Fallback: derive categoryId from contextLabel (e.g., "Personal Care" -> "personal-care")
      if (categoryId === 'other-expenses' && task.contextLabel) {
        categoryId = task.contextLabel.toLowerCase().replace(/\s+/g, '-');
      }

      console.log('[RecordTransactionModal] Using categoryId:', categoryId, 'from envelope:', task.linkedEnvelopeId);

      // Build transaction data from task
      const transactionData = {
        amount: parseFloat(amount),
        categoryId: categoryId,
        categoryType: 'expense',
        date: date,
        description: task.title,
        notes: task.description || `From completed task: ${task.title}`,
        envelopeId: task.linkedEnvelopeId || null,
        metadata: {
          source: 'task_completion',
          taskId: task.id,
          taskTitle: task.title
        }
      };

      await createTransaction(transactionData);
      
      // Refresh envelope data if context available
      if (categoryEnvelopeContext?.refreshEnvelopes) {
        await categoryEnvelopeContext.refreshEnvelopes();
      }

      toast.success('Transaction recorded! 💰');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error recording transaction:', error);
      toast.error('Failed to record transaction');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  if (!isOpen || !task) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleSkip}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#006D77] to-[#118AB2] p-6 text-white">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Receipt size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Record as Transaction?</h2>
                  <p className="text-white/80 text-sm mt-0.5">
                    Sync this expense to your budget
                  </p>
                </div>
              </div>
              <button
                onClick={handleSkip}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Task Info */}
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={20} className="text-green-600" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{task.title}</p>
                {task.description && (
                  <p className="text-sm text-gray-600 truncate">{task.description}</p>
                )}
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Amount */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Banknote size={14} className="inline mr-1.5" />
                Amount
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg font-semibold">
                  {currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$'}
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-10 pr-4 py-3 text-xl font-bold border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#006D77] focus:border-[#006D77] transition-all"
                  autoFocus
                />
              </div>
              {task.estimatedAmount && parseFloat(amount) !== task.estimatedAmount && (
                <button
                  type="button"
                  onClick={() => setAmount(task.estimatedAmount.toString())}
                  className="mt-2 text-sm text-[#006D77] hover:text-[#005560] font-medium"
                >
                  Reset to estimated: {formatCurrency(task.estimatedAmount, currency)}
                </button>
              )}
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Calendar size={14} className="inline mr-1.5" />
                Date
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#006D77] focus:border-[#006D77] transition-all"
              />
            </div>

            {/* Linked Envelope Info */}
            {task.linkedEnvelopeId && (
              <div className="flex items-center gap-2 p-3 bg-[#006D77]/5 rounded-xl border border-[#006D77]/20">
                <Tag size={16} className="text-[#006D77]" />
                <span className="text-sm text-[#006D77] font-medium">
                  Will be recorded to your linked budget envelope
                </span>
              </div>
            )}

            {/* AI Tip */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
              <Sparkles size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">
                Recording tasks as transactions helps FinList track your spending patterns 
                and give you better budget insights!
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleSkip}
                className="flex-1 px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Skip for now
              </button>
              <button
                type="submit"
                disabled={saving || !amount}
                className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-[#006D77] rounded-xl hover:bg-[#005560] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Recording...
                  </>
                ) : (
                  <>
                    <Receipt size={16} />
                    Record Transaction
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
